/**
 * This file implements the interface 'Specification' from ./types and serves as API to specification
 */
import {
    FioriElementsVersion,
    SchemaType,
    TemplateType,
    CustomExtensionType,
    defaultExportResult,
    LogSeverity
} from '@sap/ux-specification-types';
import type {
    CustomExtensionData,
    ExportResults,
    File,
    GenerateSchemaParameters,
    ExportConfigParameters,
    ImportConfigParameters,
    ImportProjectParameters,
    ApiVersion,
    UI5Version,
    GenerateCustomExtensionParams,
    ExportConfigWithPath,
    CustomSectionV2
} from '@sap/ux-specification-types';
import {
    generateListReportInterfaceV2,
    generateObjectPageInterfaceV2,
    generateOVPInterfaceV2,
    generateAnalyticalListPageInterfaceV2
} from './sync/v2/generate';
import {
    generateFreestyleInterfaceV4,
    generateListReportInterfaceV4,
    generateObjectPageInterfaceV4,
    generateFPMCustomPageInterfaceV4
} from './sync/v4/generate';
import { exportConfigEntityByPathV2, exportPageV2 } from './sync/v2/export/exportPage';
import {
    createObjectPageConfigV2,
    createOverviewPageConfig as createOverviewPageConfigV2,
    createAnalyticalListPageConfig as createAnalyticalListPageConfigV2
} from './sync/v2/import';
import { createListReportConfig as createListReportConfigV2 } from './sync/v2/genericSchemaHandling/pages/pageAccess'; // Can be switched for manual tests to use ListReportNew by switching useGenericSchemaHandling in pageAccess.ts from false to true
import { exportConfigEntityByPathV4, exportPageV4 } from './sync/v4/export/export';
import {
    createListReportConfig as createListReportConfigV4,
    createObjectPageConfigV4,
    createCustomPageConfig
} from './sync/v4/import';
import { importProjectSchemaAndConfig, importProjectSchema as importProjectSchemaOnly } from './sync/common';
import type { Editor } from 'mem-fs-editor';
import type { IdGeneratorFunction } from '@sap-ux/fe-fpm-writer';
import {
    enableFPM,
    generateControllerExtension,
    generateCustomAction,
    generateActionMenu,
    generateCustomColumn,
    generateCustomFilter,
    generateCustomHeaderSection,
    generateCustomPage,
    generateCustomSection,
    generateCustomSubSection,
    generateCustomView,
    generateObjectPage,
    generateListReport,
    PromptsAPI,
    PromptsType,
    generateCustomField,
    createIdGenerator
} from '@sap-ux/fe-fpm-writer';
import packageJson from '../package.json';
import {
    getFragmentContent,
    getViewContent,
    getViewFileName,
    ViewTypes
} from './sync/v2/export/view-controller-generator';
import { getFsInstance } from './ftfs';
import { log } from './extensionLogger';
import { join } from 'path';

const fpmWriterMap = new Map<
    CustomExtensionType,
    (basePath: string, data: CustomExtensionData, fs?: Editor) => Promise<Editor>
>([
    [CustomExtensionType.CustomPage, generateCustomPage],
    [CustomExtensionType.CustomColumn, generateCustomColumn],
    [CustomExtensionType.CustomSection, generateCustomSection],
    [CustomExtensionType.ObjectPage, generateObjectPage],
    [CustomExtensionType.ListReport, generateListReport],
    [CustomExtensionType.CustomAction, generateCustomAction],
    [CustomExtensionType.ActionMenu, generateActionMenu],
    [CustomExtensionType.CustomView, generateCustomView],
    [CustomExtensionType.ControllerExtension, generateControllerExtension],
    [CustomExtensionType.CustomSubSection, generateCustomSubSection],
    [CustomExtensionType.CustomFilterField, generateCustomFilter],
    [CustomExtensionType.CustomHeaderSection, generateCustomHeaderSection],
    [CustomExtensionType.CustomField, generateCustomField]
]);

export { readApp } from './ftfs';

/**
 * Return the generic (project independent) JSON schema.
 *
 * @param specification - reference to spec
 * @param version - Fiori elements version (v2 or v4)
 * @param schemaType - type of the schema, e.g. ListReport, ObjectPage
 * @returns the generic schema for the given Fiori Elements version and schema type
 */
export { getGenericSchema } from './specification/schemaAccess';

/**
 * API class to request and handle prompts for fpm-writer features.
 */
export const prompts = {
    api: PromptsAPI,
    types: PromptsType
};

/**
 * Generate JSON schema for a given Fiori elements version and page type.
 *
 * @param generateParameters the input structure that contains the version, schema type, and parameters
 * @returns the app-specific schema for the given schema type and page
 */
export function generateSchema(generateParameters: GenerateSchemaParameters): object {
    let schema: object;
    if (generateParameters[FioriElementsVersion.v2]) {
        /**
         * Handle v2 schema generation
         */
        const v2Params = generateParameters[FioriElementsVersion.v2];
        if (v2Params[SchemaType.ListReport]) {
            const { genericSchema, entitySet, annotations, manifest, fragments, flex, logger } =
                v2Params[SchemaType.ListReport];
            schema = generateListReportInterfaceV2(
                genericSchema,
                entitySet,
                annotations,
                manifest,
                fragments,
                flex,
                logger
            );
        } else if (v2Params[SchemaType.ObjectPage]) {
            const { genericSchema, entitySet, annotations, manifest, fragments, flex, logger } =
                v2Params[SchemaType.ObjectPage];
            schema = generateObjectPageInterfaceV2(
                genericSchema,
                entitySet,
                annotations,
                manifest,
                fragments,
                flex,
                logger
            );
        } else if (v2Params[SchemaType.OverviewPage]) {
            const { genericSchema, manifest } = v2Params[SchemaType.OverviewPage];
            schema = generateOVPInterfaceV2(genericSchema, manifest);
        } else if (v2Params[SchemaType.AnalyticalListPage]) {
            const { genericSchema, entitySet, annotations, manifest, fragments, flex, logger } =
                v2Params[SchemaType.AnalyticalListPage];
            schema = generateAnalyticalListPageInterfaceV2(
                genericSchema,
                entitySet,
                annotations,
                manifest,
                fragments,
                flex,
                logger
            );
        }
    } else if (generateParameters[FioriElementsVersion.v4]) {
        /**
         * Handle v4 schema generation
         */
        const v4Params = generateParameters[FioriElementsVersion.v4];
        if (v4Params[SchemaType.ListReport]) {
            const { genericSchema, entitySet, annotations, logger, manifest, contextPath } =
                v4Params[SchemaType.ListReport];
            schema = generateListReportInterfaceV4(
                TemplateType.ListReportObjectPageV4,
                genericSchema,
                entitySet,
                annotations,
                logger,
                manifest,
                contextPath
            );
        } else if (v4Params[SchemaType.ObjectPage]) {
            const {
                genericSchema,
                entitySet,
                annotations,
                manifest,
                logger,
                fragments,
                contextPath,
                genericMacrosSchema
            } = v4Params[SchemaType.ObjectPage];
            schema = generateObjectPageInterfaceV4(
                genericSchema,
                entitySet,
                annotations,
                manifest,
                logger,
                fragments,
                contextPath,
                genericMacrosSchema,
                generateParameters.featureToggles
            );
        } else if (v4Params[SchemaType.AnalyticalListPage]) {
            const { genericSchema, entitySet, annotations, logger, manifest, contextPath } =
                v4Params[SchemaType.AnalyticalListPage];
            //As of now, the LR logic can also be applied to ALP
            schema = generateListReportInterfaceV4(
                TemplateType.AnalyticalListPageV4,
                genericSchema,
                entitySet,
                annotations,
                logger,
                manifest,
                contextPath
            );
        } else if (v4Params[SchemaType.FreestylePage]) {
            const { genericSchema, entitySet, annotations, logger, manifest } = v4Params[SchemaType.FreestylePage];
            schema = generateFreestyleInterfaceV4(
                TemplateType.FreestylePageV4,
                genericSchema,
                annotations,
                manifest,
                entitySet,
                logger
            );
        } else if (v4Params[SchemaType.FPMCustomPage]) {
            const { genericSchema, routingId, manifest, views, logger, annotations } =
                v4Params[SchemaType.FPMCustomPage];
            schema = generateFPMCustomPageInterfaceV4(
                genericSchema,
                routingId,
                manifest,
                views,
                annotations,
                generateParameters.featureToggles,
                logger
            );
        }
    }
    return schema;
}

/**
 * Exports the whole config (src/*.json, src/pages/*.json) to flex changes and/or manifest settings.
 *
 * @param exportParameters - parameters to control the export
 * @returns a bundle of all actual objects of any artifact type
 */
export function exportConfig(exportParameters: ExportConfigParameters): ExportResults {
    let exportResults: ExportResults;
    const ui5Config: UI5Version = { ui5Version: exportParameters.ui5Version, layer: exportParameters.layer };
    if (exportParameters[FioriElementsVersion.v2]) {
        /**
         * Handle v2 export
         */
        const v2Params = exportParameters[FioriElementsVersion.v2];
        exportResults = exportPageV2(v2Params, ui5Config);
    } else if (exportParameters[FioriElementsVersion.v4]) {
        /**
         * Handle v4 export
         */
        const v4Params = exportParameters[FioriElementsVersion.v4];
        exportResults = exportPageV4(v4Params, ui5Config);
    }
    return exportResults;
}

/**
 * Exports a part of the config, specified by a propertyPath, to flex changes and/or manifest settings.
 *
 * @param exportParameters - parameters to control the export
 * @returns a bundle of all changed objects of any artifact type
 */
export function exportConfigEntityByPath(exportParameters: ExportConfigWithPath): ExportResults {
    let exportResults: ExportResults = defaultExportResult;

    const ui5Config: UI5Version = { ui5Version: exportParameters.ui5Version, layer: exportParameters.layer };
    if (exportParameters[FioriElementsVersion.v2]) {
        /**
         * Handle v2 export
         */
        const v2Params = exportParameters[FioriElementsVersion.v2];
        exportResults = exportConfigEntityByPathV2(v2Params, ui5Config);
    } else if (exportParameters[FioriElementsVersion.v4]) {
        /**
         * Handle v4 export
         */
        const v4Params = exportParameters[FioriElementsVersion.v4];
        exportResults = exportConfigEntityByPathV4(v4Params, ui5Config);
    }
    return exportResults;
}

/**
 * Exports a part of the config, specified by a propertyPath, to flex changes and/or manifest settings.
 *
 * @param exportParameters - parameters to control the export
 * @returns a bundle of all changed objects of any artifact type
 */
export function deleteConfigEntityByPath(exportParameters: ExportConfigWithPath): ExportResults {
    let exportResults: ExportResults = defaultExportResult;

    const ui5Config: UI5Version = { ui5Version: exportParameters.ui5Version, layer: exportParameters.layer };
    if (exportParameters[FioriElementsVersion.v2]) {
        /**
         * Handle v2 export
         */
        const v2Params = exportParameters[FioriElementsVersion.v2];
        exportResults = exportConfigEntityByPathV2(v2Params, ui5Config, true);
    } else if (exportParameters[FioriElementsVersion.v4]) {
        /**
         * Handle v4 export
         */
        const v4Params = exportParameters[FioriElementsVersion.v4];
        exportResults = exportConfigEntityByPathV4(v4Params, ui5Config, true);
    }
    return exportResults;
}

/**
 * Imports manifest settings and flex changes to a config (src/*.json, src/pages/*.json).
 *
 * @param importParameters - parameters to control the import
 * @returns the generated config json file
 */
export function importConfig(importParameters: ImportConfigParameters): object | undefined {
    let config;
    if (importParameters[FioriElementsVersion.v2]) {
        /**
         * Handle v2 import
         */
        const v2Params = importParameters[FioriElementsVersion.v2];
        if (v2Params[SchemaType.ListReport]) {
            config = createListReportConfigV2(v2Params[SchemaType.ListReport]);
        } else if (v2Params[SchemaType.ObjectPage]) {
            config = createObjectPageConfigV2(v2Params[SchemaType.ObjectPage]);
        } else if (v2Params[SchemaType.OverviewPage]) {
            config = createOverviewPageConfigV2(v2Params[SchemaType.OverviewPage]);
        } else if (v2Params[SchemaType.AnalyticalListPage]) {
            config = createAnalyticalListPageConfigV2(v2Params[SchemaType.AnalyticalListPage]);
        }
    } else if (importParameters[FioriElementsVersion.v4]) {
        /**
         * Handle v4 import
         */
        const v4Params = importParameters[FioriElementsVersion.v4];
        if (v4Params[SchemaType.ListReport]) {
            config = createListReportConfigV4(v4Params[SchemaType.ListReport], SchemaType.ListReport);
        } else if (v4Params[SchemaType.ObjectPage]) {
            config = createObjectPageConfigV4(v4Params[SchemaType.ObjectPage]);
        } else if (v4Params[SchemaType.AnalyticalListPage]) {
            config = createListReportConfigV4(v4Params[SchemaType.AnalyticalListPage], SchemaType.AnalyticalListPage);
        } else if (v4Params[SchemaType.FPMCustomPage]) {
            config = createCustomPageConfig(v4Params[SchemaType.FPMCustomPage]);
        }
    }
    return config;
}

/**
 * Evaluates all project files and generates app schemas and configs out of it.
 *
 * @param importParameters - parameters to control the import; here: all relevant project files
 * @returns a list of files, comprising app schemas and configs
 */
export async function importProject(importParameters: ImportProjectParameters): Promise<File[]> {
    return importProjectSchemaAndConfig(importParameters);
}

/**
 * Evaluates all project files and generates app schemas out of it, but no configs.
 *
 * @param importParameters - parameters to control the import; here: all relevant project files
 * @returns a list of files, comprising all app schemas
 */
export async function importProjectSchema(importParameters: ImportProjectParameters): Promise<File[]> {
    return importProjectSchemaOnly(importParameters);
}

/**
 * Returns specification API version.
 *
 * @returns {ApiVersion} Object of API version.
 */
export function getApiVersion(): ApiVersion {
    return {
        version: '24',
        fpmWriter: '39',
        moduleVersion: packageJson.version
    };
}
/**
 * Generates a V2 custom section view/controller artifact and writes it to the target folder.
 *
 * @param section - Custom section metadata used for file generation.
 * @param generateId - ID generator function used by view/fragment generation.
 * @param fsEditor - Optional mem-fs editor instance used to write generated content.
 * @returns The mem-fs editor instance containing the generated files.
 */
function generateCustomSectionV2(
    section: CustomSectionV2,
    generateId: IdGeneratorFunction,
    fsEditor: Editor
): Editor | undefined {
    try {
        const { name, isFragment, namespace } = section;
        const viewName = getViewFileName(name, isFragment);
        // get content for view/fragment
        const content = isFragment
            ? getFragmentContent(name, namespace, ViewTypes.XML, generateId, false)
            : getViewContent(name, namespace, ViewTypes.XML, generateId, false, false);

        fsEditor.write(join(section.folders.targetFolder, viewName), content);
        return fsEditor;
    } catch (error) {
        log(undefined, {
            severity: LogSeverity.Error,
            message: error instanceof Error ? error.message : 'Unknown error while generating custom section.'
        });
        throw error;
    }
}

/**
 * Calls the FPM Writer for generating a custom extension.
 *
 * @param customExtensionParams parameters for generating the custom extension
 * @returns Instance of mem-fs-editor
 */
export async function generateCustomExtension(
    customExtensionParams: GenerateCustomExtensionParams
): Promise<Editor | undefined> {
    const fpmWriterFunction = fpmWriterMap.get(customExtensionParams.customExtension);
    if (fpmWriterFunction && typeof fpmWriterFunction === 'function') {
        enableFPM(customExtensionParams.basePath, {});
        return fpmWriterFunction(
            customExtensionParams.basePath,
            customExtensionParams.data,
            customExtensionParams.fsEditor
        );
    } else if (customExtensionParams.customExtension === CustomExtensionType.CustomSectionV2) {
        const fsEditor = customExtensionParams.fsEditor ?? getFsInstance();
        const generateId = await createIdGenerator({ fsEditor, basePath: customExtensionParams.basePath });
        return generateCustomSectionV2(customExtensionParams.data as CustomSectionV2, generateId, fsEditor);
    } else {
        return;
    }
}
